import { useState, useEffect, useRef } from "react";
import { useBrand } from "@/contexts/BrandContext/hooks";
import { claimAutoPrompt, useAutoPromptRelease } from "@/lib/autoPrompt";

const PROMPT_ID = "dev-store-upgrade";
const STORAGE_LAST_SHOWN = "devstore_upgrade_last_shown";
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const SHOW_DELAY_MS = 2000;

export function useDevStoreUpgradePrompt() {
  const { brandDetails } = useBrand();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasScheduledRef = useRef(false);
  useAutoPromptRelease(PROMPT_ID, isModalOpen);

  // Same detection as DevStoreBanner in BrandLayout: brandDetails?.isDevStore === true
  const isDevStore = Boolean(brandDetails?.isDevStore);

  useEffect(() => {
    // Wait until brand details are loaded (same as when DevStoreBanner is shown)
    if (brandDetails == null || !isDevStore) return;

    const lastShownRaw = localStorage.getItem(STORAGE_LAST_SHOWN);
    const lastShown = lastShownRaw ? parseInt(lastShownRaw, 10) : 0;
    const now = Date.now();
    const timeSinceLastShown = now - lastShown;

    const shouldShow = timeSinceLastShown >= COOLDOWN_MS;

    if (!shouldShow) return;

    // Prevent scheduling multiple timers in the same "logical" run. In React Strict Mode
    // the effect runs twice: first run sets timer, cleanup clears it and resets ref,
    // second run can then set the timer again so the modal still shows.
    if (hasScheduledRef.current) return;
    hasScheduledRef.current = true;

    const timer = setTimeout(() => {
      hasScheduledRef.current = false;
      // Something else is on screen: skip, and try again on the next page.
      if (!claimAutoPrompt(PROMPT_ID)) return;
      setIsModalOpen(true);
    }, SHOW_DELAY_MS);

    return () => {
      clearTimeout(timer);
      hasScheduledRef.current = false;
    };
  }, [isDevStore, brandDetails]);

  const handleClose = () => {
    // "Remind me later" — record last shown so we don't show again for 24h
    try {
      localStorage.setItem(STORAGE_LAST_SHOWN, Date.now().toString());
    } catch (_) {}
    setIsModalOpen(false);
  };

  return {
    isModalOpen,
    handleClose,
  };
}
